import { createSelector } from '@ngrx/store';
import { Organization } from '../../data/providers/organization.model';
import {
  sessionInfoFeature,
  USER_ROLE,
  UserInfo,
} from '../reducers/session-info.reducer';
import { userFhirFeature } from '../reducers/user-fhir.reducer';
import { userRoleSelector } from './user-role.selectors';
import { Person } from '../../data/persons/person.model';

const USER_FHIR_RESOURCE_BY_ROLE: {
  [USER_ROLE.DPO]: null;
  [USER_ROLE.PROVIDER_VERIFIER]: null;
  [USER_ROLE.PROVIDER]: string;
  [USER_ROLE.PATIENT]: string;
  [USER_ROLE.RECORD_OWNER]: null;
  [USER_ROLE.REPRESENTATIVE]: string;
  [USER_ROLE.DATA_PRIVACY_OFFICER]: null;
} = {
  [USER_ROLE.RECORD_OWNER]: null,
  [USER_ROLE.PATIENT]: 'Patient',
  [USER_ROLE.PROVIDER]: 'Practitioner',
  [USER_ROLE.REPRESENTATIVE]: 'Person',
  [USER_ROLE.DATA_PRIVACY_OFFICER]: null,
  [USER_ROLE.DPO]: null,
  [USER_ROLE.PROVIDER_VERIFIER]: null,
};

export const userFhirResourceTypeSelector = createSelector(
  userRoleSelector,
  (userRole) => USER_FHIR_RESOURCE_BY_ROLE[userRole],
);

export const userPersonSelector = userFhirFeature.selectUserPerson;
export const userOrganizationSelector = userFhirFeature.selectUserOrganization;
export const userPractitionerProvidersSelector =
  userFhirFeature.selectUserPractitionerProviders;
export const userOrganizationProvidersSelector =
  userFhirFeature.selectUserOrganizationProviders;

export const userOrganizationProvidersByLevelSelector = (level) =>
  createSelector(
    userOrganizationProvidersSelector,
    (userOrganizationProviders: Organization[]) =>
      userOrganizationProviders.filter((organization) => {
        return organization._categories['level']
          .map(({ id }) => id)
          .includes(level);
      }),
  );

export const userFhirIdSelector = createSelector(
  sessionInfoFeature.selectUserInfo,
  userPersonSelector,
  (userInfo: UserInfo, person: Person) => person?.id || userInfo?.patient_ID,
);
export const userFhirLokalIdSelector = createSelector(
  userPersonSelector,
  (person: Person) => person?.userIdentifier,
);

export const userFhirIdentifierValueSelector = createSelector(
  sessionInfoFeature.selectUserInfo,
  (userInfo: UserInfo) => userInfo?.provider_ID?.extension,
);

export const userFhirIdentifierSystemSelector = createSelector(
  sessionInfoFeature.selectUserInfo,
  (userInfo: UserInfo) =>
    userInfo?.provider_ID?.root && `urn:oid:${userInfo?.provider_ID?.root}`,
);

export const userOrganizationFhirIdSelector = createSelector(
  sessionInfoFeature.selectUserInfo,
  (userInfo: UserInfo) =>
    null /** @todo add userOrganizationFhirResource?.id */,
);

export const userOrganizationFhirIdentifierValueSelector = createSelector(
  sessionInfoFeature.selectUserInfo,
  (userInfo: UserInfo) => userInfo?.organization_ID?.split(':')?.pop(), // user's organization is defined by identifier value
);

export const userFhirReferenceUrlSelector = createSelector(
  userFhirResourceTypeSelector,
  userFhirIdSelector,
  (resourceType, id) => resourceType && id && `${resourceType}/${id}`,
);

export const userFhirReferenceSelector = createSelector(
  userFhirReferenceUrlSelector,
  userFhirFeature.selectUserPerson,
  (referenceUrl, userPerson) =>
    referenceUrl &&
    ({
      reference: referenceUrl,
      display: userPerson?.name,
    } as fhir4.Reference),
);

export const userOrganizationFhirReferenceUrlSelector = createSelector(
  userOrganizationFhirIdSelector,
  (id) => id && `Organization/${id}`,
);

export const userOrganizationFhirReferenceSelector = createSelector(
  userOrganizationFhirReferenceUrlSelector,
  userOrganizationSelector,
  (referenceUrl, organization) =>
    referenceUrl &&
    ({
      reference: referenceUrl,
      display: organization?.name,
    } as fhir4.Reference),
);
